import { Box, Chip } from "@mui/material";
import { GridApi, GridRowId } from "@mui/x-data-grid-premium";
import { gql } from "generated-graphql";
import { ListOrganizationsQuery, ListOrganizationsQueryVariables } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useRef, useState } from "react";

import { TENANT_FEATURE_FLAG_FILTER_COLUMNS } from "./FeatureFlags/tenantFlagFilterColumns";
import { OmnisearchDataGrid } from "./OmnisearchDataGrid";
import CopyableTypography from "./Typography/CopyableTypography";

export const LIST_ORGANIZATIONS = gql(/* GraphQL */ `
  query ListOrganizations(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...tenant
      }
      count
    }
  }
`);

export type Tenant = NonNullable<NonNullable<ListOrganizationsQuery["tenants"]["items"]>[number]>;

type TenantPickerProps = {
  onTenantSelected: (tenant: Tenant) => void;
};

export function TenantPicker({ onTenantSelected }: TenantPickerProps) {
  const apiRef = useRef<GridApi | null>(null);
  const dataGridRef = useRef<HTMLDivElement | null>(null);
  const [activeId, setActiveId] = useState<GridRowId | null>(null);
  const { isOriginalUserStaff } = useCurrentUser();
  const columns = useMemo(() => {
    const defaultColumns: OmnisearchGridColDef<Tenant>[] = [
      {
        field: "name",
        headerName: "Organization Name",
        flex: 1,
      },
    ];
    if (isOriginalUserStaff) {
      const additionalColumns: OmnisearchGridColDef<Tenant>[] = [
        {
          field: "id",
          headerName: "ID",
          flex: 1.25,
          renderCell: ({ row }) => (
            <CopyableTypography text={row.id} sx={{ fontFamily: "monospace", fontSize: "1em" }} />
          ),
        },
        {
          field: "status",
          headerName: "Status",
          filterKeyType: "enumAutocomplete",
          enumValues: ["Active", "Demo", "Disabled"],
          flex: 0.4,
          renderCell: ({ row }) => {
            // Precedence: Disabled > Demo > Active.
            if (row.isDisabled) {
              return <Chip label="Disabled" color="error" size="small" variant="outlined" />;
            }
            if (row.isDemo) {
              return <Chip label="Demo" color="warning" size="small" variant="outlined" />;
            }
            return <Chip label="Active" color="success" size="small" variant="outlined" />;
          },
        },
      ];
      defaultColumns.push(...additionalColumns);
    }
    return defaultColumns;
  }, [isOriginalUserStaff]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const api = apiRef.current;
      if (!api) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
        return;
      }
      // Keep focus in the search input; just manage highlight/selection
      e.preventDefault();

      const ids = api.getSortedRowIds();
      if (!ids || ids.length === 0) return;

      if (e.key === "Enter") {
        const idForEnter = activeId ?? ids[0];
        const row: Tenant | null = idForEnter != null ? api.getRow(idForEnter) : null;
        if (row) onTenantSelected(row);
        return;
      }

      const currentIdx = activeId != null ? ids.findIndex((id) => id === activeId) : -1;
      let nextIdx: number;
      if (currentIdx === -1) {
        // No valid current selection: ArrowDown lands on first row, ArrowUp on last
        nextIdx = e.key === "ArrowDown" ? 0 : ids.length - 1;
      } else {
        nextIdx =
          e.key === "ArrowDown"
            ? (currentIdx + 1) % ids.length
            : (currentIdx - 1 + ids.length) % ids.length;
      }
      const nextId = ids[nextIdx];
      setActiveId(nextId);
      api.scrollToIndexes({ rowIndex: nextIdx });
    },
    [activeId, onTenantSelected]
  );

  return (
    <Box sx={{ pt: 1 }} onKeyDown={handleKeyDown}>
      <OmnisearchDataGrid<ListOrganizationsQuery, ListOrganizationsQueryVariables, Tenant>
        persistenceKey="tenant-picker"
        autoFocus
        sx={{ height: 510 }}
        withPadding={false}
        apiRef={apiRef}
        dataGridRef={dataGridRef}
        dataQuery={LIST_ORGANIZATIONS}
        fetchPolicy="cache-first"
        columns={columns}
        // Staff-only feature-flag filter chips. Both `Modules Enabled`
        // and `Flags Enabled` serialize to the existing `hasFlag`
        // omnisearch key on the `tenants` resolver, so no backend change
        // is needed. Hidden for non-staff users since the chip values
        // (flag keys) are an internal concern.
        additionalFilterColumns={
          isOriginalUserStaff ? TENANT_FEATURE_FLAG_FILTER_COLUMNS : undefined
        }
        persistSortModel={false}
        initialSortModel={
          isOriginalUserStaff
            ? [{ field: "status", sort: "asc" }]
            : [{ field: "name", sort: "asc" }]
        }
        getCount={(data) => data.tenants.count}
        getItems={(data) => data.tenants.items}
        selectedIds={activeId != null ? [activeId] : []}
        isURLDriven={false}
        onRowClick={({ row }) => {
          onTenantSelected(row);
        }}
        showManageColumns={false}
        onQueryStateChange={({ data, loading }) => {
          if (loading) return;
          // Use ids from the new query data directly. apiRef.getSortedRowIds()
          // can return stale ids while OmnisearchDataGrid's useDeferredValue
          // catches up, which prevents the first row from being highlighted
          // after a search.
          const items = data?.tenants?.items;
          if (!items || items.length === 0) {
            if (activeId != null) setActiveId(null);
            return;
          }
          const ids: GridRowId[] = items.map((item) => item.id);
          if (activeId == null || !ids.includes(activeId)) {
            setActiveId(ids[0]);
            // Ensure the first row is visible; do not steal focus from the input
            setTimeout(() => {
              apiRef.current?.scrollToIndexes({ rowIndex: 0 });
            }, 0);
          }
        }}
      />
    </Box>
  );
}
